/**
 * Authentication helper utilities
 */

import {
  API_RESPONSE_STATUS,
  PAGE_INFO_MESSAGES,
  ERROR_MESSAGES,
  AUTH_ERROR_REASON,
} from "./constants";
import type { AuthErrorResponse } from "../types/auth";

export function hasVerifyEmailAction(
  data: Record<string, unknown> | undefined
): boolean {
  if (!data) {
    return false;
  }
  return (
    typeof data.action === "string" &&
    data.action.trim().toUpperCase() === AUTH_ERROR_REASON.VERIFY_EMAIL_REQUIRED
  );
}

export function isUserNotConfirmedLoginResponse(
  data: Record<string, unknown> | undefined
): boolean {
  if (!data) {
    return false;
  }
  const statusCodeRaw = data.statusCode;
  const statusCode =
    typeof statusCodeRaw === "number"
      ? statusCodeRaw
      : typeof statusCodeRaw === "string"
        ? Number(statusCodeRaw)
        : NaN;
  if (statusCode !== 403) {
    return false;
  }
  const status = data.status;
  if (
    status !== undefined &&
    status !== "ERROR" &&
    status !== API_RESPONSE_STATUS.ERROR
  ) {
    return false;
  }
  const message =
    typeof data.message === "string" ? data.message.trim().toLowerCase() : "";
  if (!message) {
    return false;
  }
  return (
    message === PAGE_INFO_MESSAGES.AUTH_VERIFICATION.USER_NOT_CONFIRMED ||
    hasVerifyEmailAction(data)
  );
}

export function parseAuthStatusCode(
  data: Record<string, unknown> | undefined
): number {
  if (!data) {
    return NaN;
  }
  const statusCodeRaw = data.statusCode;
  if (typeof statusCodeRaw === "number") {
    return statusCodeRaw;
  }
  if (typeof statusCodeRaw === "string") {
    const num = Number(statusCodeRaw);
    if (Number.isFinite(num)) {
      return num;
    }
  }
  return NaN;
}

export function isUserNotFoundLoginResponse(
  data: Record<string, unknown> | undefined
): boolean {
  if (!data) {
    return false;
  }
  const statusCode = parseAuthStatusCode(data);
  if (statusCode !== 404) {
    return false;
  }
  const message =
    typeof data.message === "string" ? data.message.trim().toLowerCase() : "";
  const detail =
    typeof data.data === "string" ? data.data.trim().toLowerCase() : "";
  const haystack = `${message} ${detail}`;
  return (
    message.includes("user not found") ||
    haystack.includes("does not exist") ||
    haystack.includes("user does not exist")
  );
}

export function isBlockedByAdminLoginResponse(
  data: Record<string, unknown> | undefined
): boolean {
  if (!data) {
    return false;
  }
  if (data.isBlockedByAdmin === true) {
    return true;
  }
  const statusCode = parseAuthStatusCode(data);
  if (statusCode !== 403) {
    return false;
  }
  const message =
    typeof data.message === "string" ? data.message.trim().toLowerCase() : "";
  if (!message) {
    return false;
  }
  return message.includes("suspended") || message.includes("blocked");
}

function normalizeOAuthErrorText(value: string | null | undefined): string {
  if (!value) {
    return "";
  }
  try {
    return decodeURIComponent(value.replace(/\+/g, " ")).trim().toLowerCase();
  } catch {
    return value.replace(/\+/g, " ").trim().toLowerCase();
  }
}

export function resolveCognitoOAuthCallbackError(
  error: string | null,
  errorDescription: string | null
): AuthErrorResponse | null {
  const description = normalizeOAuthErrorText(errorDescription);
  if (
    description.includes("not confirmed") ||
    description.includes("user is not confirmed")
  ) {
    return {
      message: ERROR_MESSAGES.SSO_ACCOUNT_NOT_CONFIRMED,
      reason: AUTH_ERROR_REASON.USER_NOT_CONFIRMED,
    };
  }
  return null;
}

export function isEmailRegisteredViaSsoSignupResponse(
  data: Record<string, unknown> | undefined
): boolean {
  if (!data) {
    return false;
  }
  if (parseAuthStatusCode(data) !== 409) {
    return false;
  }
  const message =
    typeof data.message === "string" ? data.message.trim().toLowerCase() : "";
  return /registered via (google|facebook)/i.test(message);
}

export function getForgotPasswordRejectedMessage(error: unknown): string {
  const err = error as {
    response?: { status?: number; data?: Record<string, unknown> };
    message?: string;
  };
  const data = err.response?.data;
  const apiMessage =
    data && typeof data.message === "string" ? data.message : "";
  const statusCode =
    typeof data?.statusCode === "number"
      ? data.statusCode
      : err.response?.status;
  const dataDetail =
    typeof data?.data === "string" ? data.data : "";

  const haystack = `${apiMessage} ${dataDetail}`.toLowerCase();
  const isUnknownUser =
    statusCode === 404 ||
    haystack.includes("user not found") ||
    haystack.includes("username/client id combination not found");

  if (isUnknownUser) {
    return ERROR_MESSAGES.FORGOT_PASSWORD_NO_ACCOUNT_FOR_EMAIL;
  }

  return (
    apiMessage ||
    err.message ||
    ERROR_MESSAGES.ERROR_RESET_PASSWORD
  );
}

export const getEmailFromContext = (
  emailFromQuery: string,
  formikValues: any
): string => {
  if (emailFromQuery) {
    return emailFromQuery;
  }
  if (formikValues && "email" in formikValues && formikValues.email) {
    return formikValues.email;
  }
  return "";
};

export const validateOtp = (
  otpValues: (number | undefined)[]
): {
  isValid: boolean;
  otp: string | null;
  error: string | null;
} => {
  const enteredOtp = otpValues.filter(
    (value) => value !== undefined && !isNaN(value as number)
  );

  if (enteredOtp.length !== 6) {
    return {
      isValid: false,
      otp: null,
      error: "Please enter a valid OTP",
    };
  }

  return {
    isValid: true,
    otp: enteredOtp.join(""),
    error: null,
  };
};

export const getPasswordFromFormik = (formikValues: any): string => {
  if (formikValues && "password" in formikValues) {
    return formikValues.password || "";
  }
  return "";
};
